
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { GameSelector } from '../../components/GameSelector';
import { getGameData } from '../../data/gameData/registry';

export const GlossaryPage: React.FC = () => {
  const { gameSlug } = useParams<{ gameSlug: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<{ glossary: any[], faq: any[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [isChangingGame, setIsChangingGame] = useState(false);

  useEffect(() => {
    const loadContent = () => {
      if (!gameSlug) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const data = getGameData(gameSlug);
        if (data) {
          setContent({
            glossary: data.glossary || [],
            faq: data.faq || []
          });
        } else {
          setError(`Glossário para "${gameSlug}" não encontrado.`);
        }
      } catch (err) {
        console.error('Failed to load glossary:', err);
        setError(`Glossário para "${gameSlug}" não encontrado.`);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [gameSlug]);

  const categories = useMemo(() => {
    if (!content) return ['All'];
    const cats = Array.from(new Set(content.glossary.map(item => item.category)));
    return ['All', ...cats].sort();
  }, [content]);

  const filteredGlossary = useMemo(() => {
    if (!content) return [];
    return content.glossary.filter(item => {
      const matchesSearch = item.term.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           item.definition.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [content, searchTerm, activeCategory]);

  if (!gameSlug || isChangingGame) {
    return (
      <div className="max-w-5xl mx-auto py-12 px-4 space-y-6">
        {isChangingGame && gameSlug && (
          <button
            onClick={() => setIsChangingGame(false)}
            className="text-xs text-slate-400 hover:text-white flex items-center gap-2 mb-4"
          >
            <i className="fas fa-arrow-left"></i> Voltar ao glossário de {gameSlug}
          </button>
        )}
        <GameSelector
          onSelectGame={(id, game) => {
            setIsChangingGame(false);
            navigate(`/glossario/${game?.slug || id}`);
          }}
          title="Enciclopédia de Card Games"
          subtitle="Selecione uma franquia de TCG para consultar termos técnicos e regras oficiais:"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 space-y-10">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto text-amber-500 text-2xl">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">{error}</h2>
            <p className="text-slate-400 text-sm">Este jogo ainda não possui termos documentados. Escolha outro jogo abaixo:</p>
          </div>
        </div>

        <GameSelector
          onSelectGame={(id, game) => {
            setError(null);
            navigate(`/glossario/${game?.slug || id}`);
          }}
          title="Escolha outro Card Game"
          subtitle="Selecione um jogo para ver os termos e regras:"
        />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 space-y-12 animate-in fade-in duration-700">
      {/* Retro Header Section */}
      <div className="text-center space-y-4">
        <div className="inline-block bg-purple-600 px-4 py-1 skew-x-[-12deg] mb-2 shadow-[4px_4px_0px_0px_rgba(168,85,247,0.4)]">
          <span className="text-[10px] font-black text-white uppercase tracking-[0.3em] skew-x-[12deg] block">
            Game Encyclopedia
          </span>
        </div>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white italic tracking-tighter uppercase leading-none pb-2">
          {gameSlug?.toUpperCase()} <span className="text-purple-500">GLOSSARY</span>
        </h1>
        <div className="h-1 w-32 bg-purple-500 mx-auto rounded-full"></div>

        <div className="pt-2">
          <button
            onClick={() => setIsChangingGame(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 border border-slate-800 hover:border-purple-500 text-slate-300 hover:text-white text-xs font-bold transition-all shadow-lg shadow-purple-900/10"
          >
            <i className="fas fa-arrows-rotate text-purple-400"></i>
            <span>Trocar Jogo</span>
          </button>
        </div>
      </div>

      {/* Controls: Search & Category Filter */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-[32px] space-y-6 shadow-2xl">
        <div className="relative">
          <i className="fas fa-search absolute left-5 top-1/2 -translate-y-1/2 text-slate-500"></i>
          <input 
            type="text" 
            placeholder="Search terms or mechanics..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 rounded-2xl pl-12 pr-6 py-4 text-white focus:outline-none focus:border-purple-500 transition-all font-medium"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all border ${
                activeCategory === cat 
                  ? 'bg-purple-600 border-purple-500 text-white shadow-lg shadow-purple-600/20' 
                  : 'bg-slate-950 border-slate-800 text-slate-500 hover:text-white hover:border-slate-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Glossary Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence mode="popLayout">
          {filteredGlossary.map((item, idx) => (
            <motion.div
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              key={`${item.term}-${idx}`}
              className="bg-slate-900/50 border border-slate-800 p-6 rounded-2xl hover:border-purple-500/30 transition-all group flex flex-col h-full"
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-black text-white italic tracking-tight group-hover:text-purple-400 transition-colors">
                  {item.term}
                </h3>
                <span className="text-[9px] font-black text-purple-500 bg-purple-500/10 px-2 py-1 rounded uppercase tracking-tighter">
                  {item.category}
                </span>
              </div>
              <p className="text-slate-400 text-xs leading-relaxed flex-1">
                {item.definition}
              </p>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {filteredGlossary.length === 0 && (
        <div className="text-center py-20 bg-slate-900/20 border border-slate-800 border-dashed rounded-3xl">
          <p className="text-slate-500 font-bold italic">No terms found matching your criteria.</p>
        </div>
      )}

      {/* FAQ Section */}
      {content && content.faq.length > 0 && (
        <section className="space-y-8 pt-12">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">Frequently Asked Questions</h2>
            <div className="flex-1 h-px bg-slate-800"></div>
          </div>
          <div className="space-y-4">
            {content.faq.map((item, idx) => (
              <div key={`faq-${idx}`} className="bg-slate-900/30 border border-slate-800 p-6 rounded-3xl space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-purple-600/20 flex items-center justify-center text-purple-400 shrink-0 text-xs font-black">?</div>
                  <h4 className="text-sm font-bold text-white leading-snug">{item.q}</h4>
                </div>
                <div className="pl-9 text-xs text-slate-400 leading-relaxed">
                  {item.a}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
